import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, ChevronDown, X } from 'lucide-react';
// ── Credential types ─────────────────────────────────────────────────────────
type CredentialType = 'email' | 'apikey' | 'sql-string' | 'sql-fields';
const CREDENTIAL_TYPES: {
  id: CredentialType;
  label: string;
}[] = [{
  id: 'email',
  label: 'Email & Password'
}, {
  id: 'apikey',
  label: 'API Key'
}, {
  id: 'sql-string',
  label: 'SQL (connection string)'
}, {
  id: 'sql-fields',
  label: 'SQL (fields)'
}];
const TYPE_LABEL: Record<CredentialType, string> = {
  email: 'Email & Password',
  apikey: 'API Key',
  'sql-string': 'SQL (connection string)',
  'sql-fields': 'SQL (fields)'
};
// Shared field shell — label above an input, with optional helper text below.
function Field({
  label,
  children,
  helper
}: {
  label: string;
  children: React.ReactNode;
  helper?: string;
}) {
  return <div className="flex flex-col" data-id="element-2841" data-mp="lxn716h4yaat">
      <label className="text-sm font-semibold text-neutral-900 mb-1.5" data-id="element-2842" data-mp="bs34p2usuhfd">
        {label}
      </label>
      {children}
      {helper && <p className="mt-1.5 text-xs text-neutral-400 leading-relaxed" data-id="element-2843" data-mp="gpbnn5nrupho">
          {helper}
        </p>}
    </div>;
}
const inputClass = 'w-full px-3 py-2.5 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-400 focus:ring-2 focus:ring-neutral-900/5 transition-colors';
// Per-type form body. Each renders only the fields that type needs.
function CredentialFields({
  type
}: {
  type: CredentialType;
}) {
  return <div className="flex flex-col gap-6" data-id="element-2844" data-mp="20ypc217ouk0">
      <Field label="Label" helper="Optional — defaults to the email / host if left blank." data-id="element-2845" data-mp="1zxbxqwq3vz4">
        <input type="text" placeholder={TYPE_LABEL[type]} className={inputClass} data-id="element-2846" data-mp="6ujnpv6pvqjq" />
      </Field>

      {type === 'email' && <>
          <Field label="Website URL" helper="Optional — where this credential is used." data-id="element-2847" data-mp="3ah1xsa05en9e">
            <input type="url" placeholder="https://example.com" className={inputClass} data-id="element-2848" data-mp="hmtmh3blnlq0" />
          </Field>
          <Field label="Email or username" data-id="element-2849" data-mp="co8olajtle4q">
            <input type="text" placeholder="you@example.com" className={inputClass} data-id="element-2850" data-mp="cnh0eksjyioh" />
          </Field>
          <Field label="Password" data-id="element-2851" data-mp="qevhgm2wg9sw">
            <input type="password" placeholder="••••••••••" className={inputClass} data-id="element-2852" data-mp="mds3640i2lts" />
          </Field>
        </>}

      {type === 'apikey' && <Field label="API key" data-id="element-2853" data-mp="3uumxuw758zws">
          <input type="password" placeholder="••••••••••" className={inputClass} data-id="element-2854" data-mp="3utvcsfb6akkr" />
        </Field>}

      {type === 'sql-string' && <Field label="Connection string" helper="e.g. postgresql://user:password@host:5432/dbname — parsed into host/port/db on save." data-id="element-2855" data-mp="7ow06ldavz01">
          <input type="password" placeholder="••••••••••" className={inputClass} data-id="element-2856" data-mp="2pjbdv2vchut" />
        </Field>}

      {type === 'sql-fields' && <>
          <div className="grid grid-cols-[1fr_120px] gap-3" data-id="element-2857" data-mp="1j27y6fmdpnea">
            <Field label="Host" data-id="element-2858" data-mp="1e5rb7oqx1f2s">
              <input type="text" placeholder="db.example.com" className={inputClass} data-id="element-2859" data-mp="1e4py7eqcaiif" />
            </Field>
            <Field label="Port" data-id="element-2860" data-mp="1mo2zikixxkcx">
              <input type="text" placeholder="5432" className={inputClass} data-id="element-2861" data-mp="1qnbufsppvoa2" />
            </Field>
          </div>
          <Field label="Database" data-id="element-2862" data-mp="1coc6kna13dey">
            <input type="text" placeholder="mydb" className={inputClass} data-id="element-2863" data-mp="1cpdh8qn4txw5" />
          </Field>
          <div className="grid grid-cols-2 gap-3" data-id="element-2864" data-mp="1hnos94p5yefy">
            <Field label="User" data-id="element-2865" data-mp="25cq28wdn9xnc">
              <input type="text" placeholder="user" className={inputClass} data-id="element-2866" data-mp="25dj1lca9xkvg" />
            </Field>
            <Field label="Password" data-id="element-2867" data-mp="2aaj63irh6fkg">
              <input type="password" placeholder="••••••••••" className={inputClass} data-id="element-2868" data-mp="2adnih3f3ghm7" />
            </Field>
          </div>
          <Field label="SSL mode" helper="Optional — e.g. require, verify-full, disable." data-id="element-2869" data-mp="20lritnxd0jfv">
            <input type="text" placeholder="require" className={inputClass} data-id="element-2870" data-mp="20jetuqt6p8tb" />
          </Field>
        </>}

      <Field label="Shared with spaces" helper="Members of these spaces can use this connection." data-id="element-2871" data-mp="2t227xm9rjnkw">
        <input type="text" placeholder="Search spaces…" className={inputClass} data-id="element-2872" data-mp="14g9dyb2wnbkd" />
      </Field>
    </div>;
}
// Right-hand slide-over panel for creating a new custom credential.
function CredentialPanel({
  type,
  onClose
}: {
  type: CredentialType;
  onClose: () => void;
}) {
  // Close on Escape for keyboard accessibility.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return <>
      {/* Scrim */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onClick={onClose} className="fixed inset-0 z-40 bg-neutral-900/30" aria-hidden="true" data-id="element-2873" data-mp="1eefdwn55i7ml" />

      {/* Panel */}
      <motion.aside role="dialog" aria-modal="true" aria-label="New custom credential" initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      stiffness: 320,
      damping: 34
    }} className="fixed top-0 right-0 z-50 flex h-full w-full max-w-[460px] flex-col bg-white shadow-2xl" data-id="element-2874" data-mp="26w14ufrfw9g3">
        {/* Header */}
        <div className="flex items-start justify-between px-7 pt-7 pb-5" data-id="element-2875" data-mp="2jbr355eyyqja">
          <div data-id="element-2876" data-mp="2ocosc1yeckua">
            <p className="text-[11px] font-bold tracking-[1.5px] uppercase text-neutral-400" data-id="element-2877" data-mp="2od6nev9bot0m">
              New connection · {TYPE_LABEL[type]}
            </p>
            <h2 className="mt-1.5 text-xl font-bold text-neutral-900" data-id="element-2878" data-mp="2xctsbr19ggyf">
              New custom credential
            </h2>
          </div>
          <button onClick={onClose} aria-label="Close" className="text-neutral-400 hover:text-neutral-700 transition-colors" data-id="element-2879" data-mp="3254pim4m9ona">
            <X size={20} data-id="element-2880" data-mp="374zfmngkia7a" />
          </button>
        </div>

        {/* Scrollable form */}
        <div className="flex-1 overflow-y-auto px-7 pb-7" data-id="element-2881" data-mp="3c3u9ckg2pj66">
          <CredentialFields type={type} data-id="element-2882" data-mp="3g2t8tpj1cnq8" />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-4 border-t border-neutral-100 px-7 py-4" data-id="element-2883" data-mp="1gyl3312im7b6">
          <button onClick={onClose} className="text-sm font-medium text-neutral-500 hover:text-neutral-800 transition-colors" data-id="element-2884" data-mp="1c09wcz7iocx6">
            Cancel
          </button>
          <button onClick={onClose} className="rounded-lg bg-[#2B211A] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#1A1410] transition-colors" data-id="element-2885" data-mp="1pt9bh03e0547">
            Connect
          </button>
        </div>
      </motion.aside>
    </>;
}
export function CustomCredentialFlow() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeType, setActiveType] = useState<CredentialType | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  // Close the dropdown on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [menuOpen]);
  const handleSelect = (type: CredentialType) => {
    setMenuOpen(false);
    setActiveType(type);
  };
  return <div className="relative" ref={menuRef} data-id="element-2886" data-mp="3mmtwsc0prda0">
      <button onClick={() => setMenuOpen(o => !o)} aria-haspopup="menu" aria-expanded={menuOpen} className="flex items-center gap-1.5 text-sm font-medium text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-1.5 transition-colors shadow-sm" data-id="element-2887" data-mp="3mjy16ucc90oq">
        <Plus size={16} data-id="element-2888" data-mp="3ho8yii50v3o7" />
        Add custom
        <ChevronDown size={14} className={`ml-0.5 transition-transform ${menuOpen ? 'rotate-180' : ''}`} data-id="element-2889" data-mp="33npr9d8ds1n3" />
      </button>

      <AnimatePresence data-id="element-2890" data-mp="38naq7nnjw9zt">
        {menuOpen && <motion.div role="menu" initial={{
        opacity: 0,
        y: -6,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -6,
        scale: 0.98
      }} transition={{
        duration: 0.15,
        ease: [0.16, 1, 0.3, 1]
      }} className="absolute right-0 top-full mt-2 w-64 origin-top-right rounded-xl border border-neutral-200 bg-white py-2 shadow-[0_16px_48px_-12px_rgba(15,23,42,0.2)] z-50" data-id="element-2891" data-mp="38odhvxsp6ggs">
            <p className="px-4 pt-1 pb-2 text-[11px] font-bold tracking-[1.5px] uppercase text-neutral-400" data-id="element-2892" data-mp="25nflipo4qhu1">
              Credential type
            </p>
            {CREDENTIAL_TYPES.map(t => <button key={t.id} role="menuitem" onClick={() => handleSelect(t.id)} className="block w-full px-4 py-2.5 text-left text-sm font-medium text-neutral-700 hover:bg-neutral-50 hover:text-neutral-900 transition-colors" data-id="element-2893" data-mp="1wqez2okmett8">
                {t.label}
              </button>)}
          </motion.div>}
      </AnimatePresence>

      <AnimatePresence data-id="element-2894" data-mp="1irffhvbyt53x">
        {activeType && <CredentialPanel type={activeType} onClose={() => setActiveType(null)} data-id="element-2895" data-mp="3h4yy38nlnx59" />}
      </AnimatePresence>
    </div>;
}